/**
 * Creates a WAV Blob from an array of Int16Array PCM chunks.
 * Pure function — no external dependencies.
 *
 * @param {Int16Array[]} chunks - Array of PCM audio chunks
 * @param {number} sampleRate - Sample rate in Hz (default 48000)
 * @param {number} numChannels - Number of audio channels (default 1 = mono)
 * @param {number} bitsPerSample - Bits per sample (default 16)
 * @returns {Blob} WAV file as a Blob
 */
export function createWavBlob(chunks, sampleRate = 48000, numChannels = 1, bitsPerSample = 16) {
  let totalSamples = 0;
  for (const chunk of chunks) {
    totalSamples += chunk.length;
  }

  const bytesPerSample = bitsPerSample / 8;
  const dataLength = totalSamples * bytesPerSample;
  const headerLength = 44;
  const totalLength = headerLength + dataLength;

  const buffer = new ArrayBuffer(totalLength);
  const view = new DataView(buffer);

  // RIFF chunk descriptor
  writeString(view, 0, 'RIFF');
  view.setUint32(4, totalLength - 8, true);
  writeString(view, 8, 'WAVE');

  // fmt sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);                                          // Sub-chunk size (16 for PCM)
  view.setUint16(20, 1, true);                                           // Audio format (1 = PCM)
  view.setUint16(22, numChannels, true);                                 // Channels
  view.setUint32(24, sampleRate, true);                                  // Sample rate
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);   // Byte rate
  view.setUint16(32, numChannels * bytesPerSample, true);                // Block align
  view.setUint16(34, bitsPerSample, true);                               // Bits per sample

  // data sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);

  // Write PCM samples from all chunks
  const int16View = new Int16Array(buffer, headerLength);
  let offset = 0;
  for (const chunk of chunks) {
    int16View.set(chunk, offset);
    offset += chunk.length;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}
